import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";

// Define gender as a union type for better type safety
type Gender = "girl" | "boy" | "other" | "";

interface ChildProfile {
  id: number;
  name: string;
  gender: string;
  birthday: string;
  language: string;
  isDefault: boolean;
  avatarUrl?: string;
}

interface GenderContextType {
  selectedGender: Gender;
  setSelectedGender: (gender: Gender) => void;
  selectedChild: ChildProfile | null;
  setSelectedChild: (child: ChildProfile | null) => void;
  resetGender: () => void;
  resetGenderAfterStory: () => void;
  syncProfileGender: () => void;
  getEffectiveGender: () => Gender;
  getPersonalizedName: () => string;
  isOverridden: boolean;
}

const GenderContext = createContext<GenderContextType | undefined>(undefined);

export const useGender = () => {
  const context = useContext(GenderContext);
  if (context === undefined) {
    throw new Error("useGender must be used within a GenderProvider");
  }
  return context;
};

interface GenderProviderProps {
  children: ReactNode;
}

export const GenderProvider = ({ children }: GenderProviderProps) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedGender, setSelectedGender] = useState<Gender>("");
  const [profileGender, setProfileGender] = useState<Gender>("");
  const [selectedChild, setSelectedChild] = useState<ChildProfile | null>(null);
  const [isOverridden, setIsOverridden] = useState(false);
  const [hasGeneratedStory, setHasGeneratedStory] = useState(false);

  // Fetch child profiles to get the default child's gender
  const { data: childProfiles = [] } = useQuery<ChildProfile[]>({
    queryKey: ['/api/child-profiles'],
    enabled: isAuthenticated,
  });

  // Initialize gender and selected child from default child profile when data loads
  useEffect(() => {
    if (isAuthenticated && Array.isArray(childProfiles) && childProfiles.length > 0) {
      // Find the default child profile
      const defaultChild = childProfiles.find((child) => child.isDefault) || childProfiles[0];
      if (defaultChild?.gender) {
        const childGender = defaultChild.gender as Gender;
        setProfileGender(childGender);
        // Only set selectedGender if it hasn't been overridden by story wizard
        if (!isOverridden) {
          setSelectedGender(childGender);
          setSelectedChild(defaultChild);
        }
      }
    } else if (!isOverridden) {
      setProfileGender("");
      setSelectedGender("");
      setSelectedChild(null);
    }
  }, [childProfiles, isAuthenticated, isOverridden]);

  const resetGender = () => {
    // Reset to profile gender if available, otherwise empty
    setSelectedGender(profileGender);
    setIsOverridden(false);
    setHasGeneratedStory(false);
  };

  const resetGenderAfterStory = () => {
    // Reset to profile gender after story generation is complete
    setSelectedGender(profileGender);
    setIsOverridden(false);
    setHasGeneratedStory(false);
  };

  // Function to sync profile gender changes to context
  const syncProfileGender = () => {
    if (user?.gender) {
      const userGender = user.gender as Gender;
      setProfileGender(userGender);
      // If not overridden by story wizard, update selectedGender immediately
      if (!isOverridden) {
        setSelectedGender(userGender);
      }
    }
  };

  // Get the effective gender (considering overrides and fallbacks)
  const getEffectiveGender = (): Gender => {
    // If selectedGender is set (either by story wizard or profile), use it
    if (selectedGender && selectedGender !== "") {
      return selectedGender;
    }
    // Fall back to profile gender
    return profileGender || "";
  };

  const handleSetSelectedGender = (gender: Gender) => {
    setSelectedGender(gender);
    // Mark as overridden if gender is different from profile gender
    setIsOverridden(gender !== profileGender && gender !== "");
    if (gender !== "") {
      setHasGeneratedStory(false); // Reset story flag when new gender is selected
    }
  };

  const handleSetSelectedChild = (child: ChildProfile | null) => {
    setSelectedChild(child);
    if (child) {
      setSelectedGender(child.gender as Gender);
      setIsOverridden(true);
    }
  };

  // Get personalized name based on selected child or user
  const getPersonalizedName = (): string => {
    // If a child is selected, use their first name
    if (selectedChild?.name) {
      const firstName = selectedChild.name.split(' ')[0];
      return firstName;
    }
    
    // Fall back to user's nickname or first name
    if (user?.nickname) return user.nickname;
    if (user?.firstName) return user.firstName;
    
    return "Your";
  };

  const value = {
    selectedGender: getEffectiveGender(),
    setSelectedGender: handleSetSelectedGender,
    selectedChild,
    setSelectedChild: handleSetSelectedChild,
    resetGender,
    resetGenderAfterStory,
    syncProfileGender,
    getEffectiveGender,
    getPersonalizedName,
    isOverridden,
  };

  return (
    <GenderContext.Provider value={value}>{children}</GenderContext.Provider>
  );
};